import { Box } from '@coolblue-development/becky';
import { Loader, Tooltip } from '@coolblue-development/freddy';
import { Cross } from '@coolblue-development/icons';
import { Attachment } from '../../../../../../models/Attachment';
import BaseAttachmentChip from '../../BaseAttachmentChip';
import { PendingUpload } from './useAttachmentUpload';

// Attachments cannot be removed individually yet (see CSD-3156). Until that ships,
// the chip stays non-removable and this tooltip explains the workaround.
const REMOVE_ATTACHMENT_TOOLTIP = 'To remove the attachment, delete this draft and start a new reply';

interface DraftAttachmentChipsProps {
  attachments: Attachment[];
  pendingUploads?: PendingUpload[];
  onDismiss?: (id: string) => void;
}

const DraftAttachmentChips = ({
  attachments,
  pendingUploads = [],
  onDismiss
}: DraftAttachmentChipsProps): JSX.Element | null => {
  if (attachments.length === 0 && pendingUploads.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        marginBottom: '12px'
      }}
      data-testid="attachment-chips-container"
    >
      {attachments.map(attachment => (
        <Tooltip key={attachment.id} description={REMOVE_ATTACHMENT_TOOLTIP} placement="top">
          <span tabIndex={0}>
            <BaseAttachmentChip fileName={attachment.name} dataTestId={`attachment-chip-${attachment.id}`} />
          </span>
        </Tooltip>
      ))}

      {pendingUploads.map(upload =>
        upload.status === 'uploading' ? (
          <BaseAttachmentChip
            key={upload.id}
            variant="loading"
            fileName={upload.name}
            dataTestId={`attachment-chip-uploading-${upload.id}`}
            trailing={
              <Box sx={{ position: 'relative', marginRight: '8px' }}>
                <Loader size="small" />
              </Box>
            }
          />
        ) : (
          <BaseAttachmentChip
            key={upload.id}
            variant="error"
            fileName={upload.name}
            dataTestId={`attachment-chip-error-${upload.id}`}
            trailing={
              <button
                type="button"
                aria-label={`Dismiss ${upload.name}`}
                onClick={() => onDismiss?.(upload.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  border: 'none',
                  background: 'none',
                  padding: 0,
                  cursor: 'pointer'
                }}
              >
                <Cross color="#D71F1F" />
              </button>
            }
          />
        )
      )}
    </Box>
  );
};

export default DraftAttachmentChips;
